import { useQuery } from '@tanstack/react-query';

import { canAccess } from '@/lib/access';
import { useAuth } from '@/lib/use-auth';
import { followUpCountQuery } from '@/pages/sparky/sparky-queries';
import { useRefetchOnRealtime } from '@/realtime/useRefetchOnRealtime';
import {
  NOTIFICATIONS_CHANGED_EVENT,
  adminAllergyChangeRequestsApi,
  adminAllergyChangeRequestsKeys,
  adminTeachersKeys,
  contactInquiriesApi,
  contactInquiriesKeys,
  edpApi,
  edpKeys,
  incidentsKeys,
  itTicketsApi,
  itTicketsKeys,
  leaveRequestKeys,
  leaveRequestsApi,
  lunchDetentionAttentionCount,
  parentProfileChangesApi,
  parentProfileChangesKeys,
  teachersApi,
  tourRequestsApi,
  tourRequestsKeys,
  useIncidentUnreadCountQuery,
  useLunchDetentionBadgesQuery,
  useMyMissingNewsletterCountQuery,
  useStudentSuspensionUnreadCountQuery,
} from '@/services';

/**
 * One badge source: the nav row it decorates, its translation key, and what's outstanding.
 *
 * `href` is the sidebar row's own path, because that is what `byHref` is looked up by. `tab` is
 * set when the count belongs to one tab of a tabbed page: a click then lands on that tab (`to`),
 * and the page can wear the count on it (`byTab`), instead of the reader guessing which tab the
 * row's number is in.
 */
export type NavBadgeEntry = { href: string; tab?: string; to?: string; labelKey: string; count: number };

export type NavBadges = {
  /** Counts keyed by nav href — what the sidebar rows read. */
  byHref: Record<string, number>;
  /** Counts keyed by nav href, then by tab — what a tabbed page hangs on its strip. */
  byTab: Record<string, Record<string, number>>;
  /** Every source in sidebar order, zeros included — the bell filters for itself. */
  entries: NavBadgeEntry[];
  /** Grand total across all sources, for the AppBar bell. */
  total: number;
};

/**
 * Pending/attention counts for sidebar nav items and the AppBar bell. Each query is
 * gated by the same `canAccess` predicate as the nav item it decorates, and
 * reuses the list pages' query keys so the badge and the page share one cache
 * entry. Returns 0 for items the user can't access or that have nothing pending.
 */
export function useNavBadges(): NavBadges {
  const { user } = useAuth();

  const leave = useQuery({
    queryKey: leaveRequestKeys.adminList({ status: 'pending' }),
    queryFn: () => leaveRequestsApi.adminList({ status: 'pending' }),
    enabled: canAccess(user, 'leave-requests'),
    select: rows => rows.length,
  });

  const contact = useQuery({
    queryKey: contactInquiriesKeys.list(),
    queryFn: () => contactInquiriesApi.getAll(),
    enabled: canAccess(user, 'contact-inquiries'),
    select: rows => rows.filter(r => r.status === 'new').length,
  });

  const tour = useQuery({
    queryKey: tourRequestsKeys.list(),
    queryFn: () => tourRequestsApi.getAll(),
    enabled: canAccess(user, 'tour-requests'),
    select: rows => rows.filter(r => r.status === 'new').length,
  });

  const teacherProfiles = useQuery({
    queryKey: adminTeachersKeys.pending,
    queryFn: () => teachersApi.listPending(),
    enabled: canAccess(user, 'teachers'),
    select: rows => rows.length,
  });

  // A dietary or allergy change sits unanswered until someone opens the page, and it is the
  // kind of thing the kitchen acts on — so it gets the same standing prompt as a pending leave
  // request rather than waiting to be discovered.
  const allergyChanges = useQuery({
    queryKey: adminAllergyChangeRequestsKeys.list('pending'),
    queryFn: () => adminAllergyChangeRequestsApi.list('pending'),
    enabled: canAccess(user, 'medical'),
    select: rows => rows.length,
  });

  useRefetchOnRealtime('allergy-changes.changed', adminAllergyChangeRequestsKeys.all);

  // Base item (no module) — visible to any admin or teacher, exactly like the endpoint's own
  // @Roles(admin, teacher) guard, whether or not they hold the sst module grant.
  const incidents = useIncidentUnreadCountQuery(canAccess(user));
  // Mounted for the whole session, so a new case rings the badge (and refreshes an open list)
  // from any page — the same moment the toast appears.
  useRefetchOnRealtime(NOTIFICATIONS_CHANGED_EVENT, incidentsKeys.all);

  // Base item too: the SST's unanswered teacher messages, or a teacher's unread SST replies.
  const suspensionThreads = useStudentSuspensionUnreadCountQuery(canAccess(user));

  // Base item as well: proposals awaiting the SST's decision, decisions the proposer hasn't
  // seen, and supervision days still to mark.
  const lunchDetentions = useLunchDetentionBadgesQuery(canAccess(user));

  // Also a base item: every staff member with timetable or homeroom slots owes newsletter
  // entries, so the count of not-yet-submitted slots in the open issue nudges them here.
  const newsletterMissing = useMyMissingNewsletterCountQuery(canAccess(user));

  // The Recent changes tab lives on the same Parents page and has no sidebar row of its
  // own — its unreviewed count folds into that page's badge, same as lessonExcusals above.
  const parentChanges = useQuery({
    queryKey: parentProfileChangesKeys.unreviewedCount(),
    queryFn: () => parentProfileChangesApi.unreviewedCount(),
    enabled: canAccess(user, 'parents'),
    select: r => r.count,
  });

  // Only `open` counts as waiting: in_progress is being handled and waiting_on_requester
  // is the requester's move, so a badge over either would never clear and become noise.
  const itTickets = useQuery({
    queryKey: itTicketsKeys.board({}),
    queryFn: () => itTicketsApi.board({}),
    enabled: canAccess(user, 'it-support'),
    select: rows => rows.filter(r => r.status === 'open').length,
  });

  useRefetchOnRealtime('it-tickets.changed', itTicketsKeys.all);

  // `changes_requested` is excluded for the same reason as waiting_on_requester above —
  // the ball is in the proposing teacher's court, not the office's.
  const edpProposals = useQuery({
    queryKey: edpKeys.queue({ status: 'submitted' }),
    queryFn: () => edpApi.queue({ status: 'submitted' }),
    enabled: canAccess(user, 'edp'),
    select: rows => rows.length,
  });

  // Observations the teacher marked "Follow up" stay open until Done; a badge keeps them from being forgotten.
  const sparkyFollowUps = useQuery({
    ...followUpCountQuery(user?.id ?? ''),
    enabled: canAccess(user, 'sparky'),
    select: r => r.count,
  });

  const entries: NavBadgeEntry[] = [
    { href: '/newsletter', labelKey: 'nav.my_work.newsletter', count: newsletterMissing.data?.count ?? 0 },
    {
      href: '/sparky',
      to: '/sparky?view=attention',
      labelKey: 'nav.sparky',
      count: sparkyFollowUps.data ?? 0,
    },
    { href: '/sst', labelKey: 'nav.sst', count: incidents.data?.count ?? 0 },
    {
      href: '/sst',
      tab: 'lunch-detention',
      labelKey: 'sst.tab_lunch_detention',
      count: lunchDetentions.data ? lunchDetentionAttentionCount(lunchDetentions.data) : 0,
    },
    {
      href: '/sst',
      tab: 'suspensions',
      labelKey: 'sst.suspensions.title',
      count: suspensionThreads.data?.count ?? 0,
    },
    { href: '/teachers', labelKey: 'nav.teachers', count: teacherProfiles.data ?? 0 },
    { href: '/inquiries', tab: 'contact', labelKey: 'nav.contact_inquiries', count: contact.data ?? 0 },
    { href: '/inquiries', tab: 'tours', labelKey: 'nav.tour_requests', count: tour.data ?? 0 },
    { href: '/leave-requests', labelKey: 'nav.leave_requests', count: leave.data ?? 0 },
    { href: '/edp', tab: 'proposals', labelKey: 'nav.edp_queue', count: edpProposals.data ?? 0 },
    { href: '/it-tickets', labelKey: 'nav.it_board', count: itTickets.data ?? 0 },
    { href: '/parents', labelKey: 'nav.parents', count: parentChanges.data ?? 0 },
    {
      href: '/medical/allergy-change-requests',
      labelKey: 'nav.allergy_change_requests',
      count: allergyChanges.data ?? 0,
    },
  ].map(e => (e.tab ? { ...e, to: `${e.href}?tab=${e.tab}` } : e));

  return {
    // Summed, not overwritten: two sources share the Inquiries row, and the row wears both.
    byHref: entries.reduce<Record<string, number>>(
      (acc, e) => ({ ...acc, [e.href]: (acc[e.href] ?? 0) + e.count }),
      {}
    ),
    byTab: entries.reduce<Record<string, Record<string, number>>>(
      (acc, e) => (e.tab ? { ...acc, [e.href]: { ...acc[e.href], [e.tab]: e.count } } : acc),
      {}
    ),
    entries,
    total: entries.reduce((sum, e) => sum + e.count, 0),
  };
}
